import React from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, GraduationCap, PlayCircle, Clock, Edit, Trash2, Users
} from 'lucide-react';
import { Button } from '../ui/button';

const EducationTab = ({
  trainingStats,
  trainingCourses,
  trainingProgress,
  selectedCourseFilter,
  setSelectedCourseFilter,
  setEditingCourse,
  setCourseForm,
  setShowCourseModal,
  setSelectedCourse,
  setShowCourseDetailModal,
  openEditCourseModal,
  handleDeleteCourse
}) => {
  // Helper function to get category color
  const getCategoryColor = (category) => {
    const colors = {
      rules: 'bg-[#F4C2C2]/15 text-purple-700',
      rides: 'bg-purple-100 text-purple-700',
      courier: 'bg-blue-100 text-blue-700',
      laundry: 'bg-pink-100 text-pink-700',
      errands: 'bg-amber-100 text-amber-700',
      safety: 'bg-[var(--error)]/15 text-red-700',
      customer_service: 'bg-amber-100 text-amber-700'
    };
    return colors[category] || 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
  };

  // Helper function to get category label
  const getCategoryLabel = (category) => {
    const labels = {
      rules: 'Company Rules',
      rides: 'Rides',
      courier: 'Courier',
      laundry: 'Laundry',
      errands: 'Errands',
      safety: 'Safety',
      customer_service: 'Customer Service'
    };
    return labels[category] || category;
  };

  // Group courses by category
  const groupedCourses = trainingCourses?.reduce((acc, course) => {
    const cat = course.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(course);
    return acc;
  }, {}) || {};

  // Filter courses
  const filteredCourses = selectedCourseFilter === 'all' 
    ? trainingCourses 
    : trainingCourses?.filter(c => c.category === selectedCourseFilter);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="education-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Worker Training</h1>
          <p className="text-[var(--text-muted)] mt-1">Video-based education with progress tracking</p>
        </div>
        <Button 
          onClick={() => {
            setEditingCourse(null);
            setCourseForm({
              title: '',
              description: '',
              category: 'rules',
              video_url: '',
              duration_minutes: 10,
              thumbnail_url: '',
              is_required: true,
              is_active: true
            });
            setShowCourseModal(true);
          }} 
          className="btn-primary" 
          data-testid="add-course-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Training Video
        </Button>
      </div>

      {/* Training Stats */}
      {trainingStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{trainingStats.total_courses}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Courses</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{trainingStats.total_assigned}</p>
            <p className="text-sm text-[var(--text-muted)]">Assigned</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{trainingStats.total_completed}</p>
            <p className="text-sm text-[var(--text-muted)]">Completed</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{trainingStats.total_in_progress}</p>
            <p className="text-sm text-[var(--text-muted)]">In Progress</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold">{trainingStats.overall_completion_rate}%</p>
            <p className="text-sm text-[var(--text-muted)]">Completion Rate</p>
          </div>
        </div>
      )}

      {/* Main Content with Sidebar Filter */}
      <div className="flex gap-6">
        {/* Category Filter - Sidebar */}
        <div className="w-48 flex-shrink-0">
          <div className="card p-4 sticky top-4">
            <h3 className="font-bold text-sm mb-3 text-[var(--text-muted)]">Categories</h3>
            <div className="flex flex-col gap-1">
              {['all', 'rules', 'rides', 'courier', 'laundry', 'errands', 'safety', 'customer_service'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCourseFilter(cat)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                    selectedCourseFilter === cat 
                      ? 'bg-[var(--primary)] text-white' 
                      : 'bg-[var(--background-subtle)] hover:bg-[var(--background-subtle)]'
                  }`}
                >
                  {cat === 'all' ? 'All Categories' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Courses Grid */}
        <div className="flex-1">
          {filteredCourses?.length === 0 ? (
            <div className="card p-12 text-center">
              <GraduationCap className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-secondary)]">No training courses yet. Add your first course.</p>
            </div>
          ) : (
        <div className="space-y-8">
          {Object.entries(
            selectedCourseFilter === 'all' ? groupedCourses : { [selectedCourseFilter]: filteredCourses }
          ).map(([category, courses]) => (
            <div key={category}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm ${getCategoryColor(category)}`}>
                  {getCategoryLabel(category)}
                </span>
                <span className="text-[var(--text-muted)] text-sm font-normal">
                  ({courses?.length || 0} courses)
                </span>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses?.map(course => {
                  const courseProgress = trainingProgress?.filter(p => p.course_id === course.id) || [];
                  const completedCount = courseProgress.filter(p => p.status === 'completed').length;
                  
                  return (
                    <div 
                      key={course.id} 
                      className="card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedCourse(course);
                        setShowCourseDetailModal(true);
                      }}
                    >
                      {/* Thumbnail */}
                      <div className="relative h-40 bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)]">
                        {course.thumbnail_url ? (
                          <img 
                            src={course.thumbnail_url} 
                            alt={course.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <PlayCircle className="w-16 h-16 text-white/50" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(course.category)}`}>
                            {getCategoryLabel(course.category)}
                          </span>
                        </div>
                        {course.is_required && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                            Required
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <h4 className="font-bold mb-2 line-clamp-1">{course.title}</h4>
                        <p className="text-sm text-[var(--text-muted)] mb-3 line-clamp-2">
                          {course.description}
                        </p>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-1 text-[var(--text-muted)]">
                            <Clock className="w-4 h-4" />
                            {course.duration_minutes} min
                          </div>
                          <div className="flex items-center gap-1 text-[var(--text-muted)]">
                            <Users className="w-4 h-4" />
                            {completedCount} completed
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-end gap-2 mt-3 pt-3 border-t" onClick={e => e.stopPropagation()}>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => openEditCourseModal(course)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleDeleteCourse(course.id)}
                            className="text-[var(--error)]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Worker Progress Section */}
      {trainingProgress?.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-bold mb-4">Worker Progress</h3>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-[var(--accent)]">
                <tr>
                  <th className="text-left p-4 font-semibold">Worker</th>
                  <th className="text-left p-4 font-semibold">Course</th>
                  <th className="text-left p-4 font-semibold">Category</th>
                  <th className="text-left p-4 font-semibold">Progress</th>
                  <th className="text-left p-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {trainingProgress.slice(0, 20).map(progress => (
                  <tr key={progress.id} className="border-t border-[var(--border-primary)]">
                    <td className="p-4">{progress.worker_name}</td>
                    <td className="p-4">{progress.course_title}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(progress.category)}`}>
                        {getCategoryLabel(progress.category)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${progress.progress_percent}%` }}
                          />
                        </div>
                        <span className="text-sm">{progress.progress_percent}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`badge-${progress.status === 'completed' ? 'success' : progress.status === 'in_progress' ? 'primary' : 'secondary'}`}>
                        {progress.status === 'not_started' ? 'Not Started' : progress.status === 'in_progress' ? 'In Progress' : 'Completed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      </div>
    </motion.div>
  );
};

export default EducationTab;
